import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSignupModal } from '../context/SignupModalContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { toast } from 'sonner';

import { BrandLogo } from '../lib/branding';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { openSignupModal } = useSignupModal();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Connexion réussie !');
      // Redirect based on role and account status
      if (user.role === 'admin') {
        navigate('/dashboard/admin');
      } else if (user.role === 'partenaire') {
        navigate('/dashboard/partner');
      } else if (user.role === 'retailer' && user.account_status === 'a_completer') {
        navigate('/dashboard/profil');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Erreur de connexion';
      
      if (error.response?.data) {
        const data = error.response.data;
        
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMessage = data.detail.map(err => err.msg).join(', ');
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with grain texture */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative ludo-grain"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1585504198199-20277593b94f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjBwbGF5aW5nJTIwYm9hcmQlMjBnYW1lc3xlbnwwfHx8fDE3ODMwNjkzMDN8MA&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10 flex items-end justify-start p-12" style={{ zIndex: 2 }}>
          <div className="text-white max-w-md">
            <h1 className="text-4xl font-heading font-bold mb-3 tracking-tight ludo-animate-fade-up">Bienvenue sur Games Visor</h1>
            <p className="text-lg text-white/80 leading-relaxed ludo-animate-fade-up ludo-stagger-2">Votre plateforme B2B dédiée aux professionnels du jeu de société et du TCG</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ background: 'var(--ludo-canvas)' }}>
        <div className="w-full max-w-md space-y-8 ludo-animate-fade-up">
          <div>
            <Link to="/" className="flex items-center mb-10 group">
              <BrandLogo size="lg" />
            </Link>
            <h2 className="text-3xl font-heading font-bold tracking-tight" style={{ color: 'var(--ludo-text)' }}>Connexion</h2>
            <p className="mt-2 text-sm" style={{ color: 'var(--ludo-text-secondary)' }}>Accédez à votre espace professionnel</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium" style={{ color: 'var(--ludo-text)' }}>Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre@email.com"
                required
                className="ludo-input h-11"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium" style={{ color: 'var(--ludo-text)' }}>Mot de passe</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium text-primary hover:underline transition-colors"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <PasswordInput
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="ludo-input h-11"
                data-testid="login-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 text-sm font-semibold rounded-lg" 
              disabled={loading} 
              data-testid="login-submit-button"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>

            <p className="text-center text-sm" style={{ color: 'var(--ludo-text-secondary)' }}>
              Pas encore de compte ?{' '}
              <button
                type="button"
                onClick={openSignupModal}
                className="font-semibold text-primary hover:underline"
                data-testid="go-to-signup-link"
              >
                Créer un compte
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
